import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { findAccountRequest } from "@/services/requestService";
import type { AccountRequestTracking } from "@/types";
import TrackHeader from "./components/TrackHeader";
import RequestStatusCard from "./components/RequestStatusCard";
import { CONTACT } from "@/config/contact";

const STORAGE_KEY = "waitechedu.last-request-ref";

type SearchState = "idle" | "loading" | "found" | "notfound" | "error";

const helpItems = [
  { icon: "ri-bookmark-line", title: "Votre référence", text: "Elle s'affiche à l'envoi de la demande (format DEM-XXXXXX) : notez-la pour suivre votre dossier." },
  { icon: "ri-time-line", title: "Délai d'examen", text: "L'administrateur de la plateforme traite généralement les demandes sous 48 h ouvrées." },
  { icon: "ri-question-line", title: "Besoin d'aide ?", text: `Écrivez à ${CONTACT.email}, notre équipe vous répond rapidement.` },
];

function TrackContent() {
  const [reference, setReference] = useState("");
  const [state, setState] = useState<SearchState>("idle");
  const [request, setRequest] = useState<AccountRequestTracking | null>(null);
  const [error, setError] = useState("");
  const [params] = useSearchParams();
  /** Référence transmise par le lien de l'e-mail (« ?ref=DEM-XXXXXX ») : recherche lancée directement. */
  const linkedRef = params.get("ref")?.trim() ?? "";

  const runSearch = async (ref: string) => {
    setError("");
    setState("loading");
    setRequest(null);
    try {
      const found = await findAccountRequest(ref);
      if (found) {
        setRequest(found);
        setState("found");
        try {
          localStorage.setItem(STORAGE_KEY, ref.trim());
        } catch {
          /* stockage indisponible */
        }
      } else {
        setState("notfound");
      }
    } catch {
      setError("Une erreur est survenue lors de la recherche. Veuillez réessayer.");
      setState("error");
    }
  };

  useEffect(() => {
    if (linkedRef) {
      setReference(linkedRef);
      void runSearch(linkedRef);
      return;
    }
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setReference(saved);
    } catch {
      /* stockage indisponible */
    }
  }, [linkedRef]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const ref = reference.trim();
    if (!ref) {
      setError("Veuillez saisir votre référence de demande.");
      setState("error");
      return;
    }
    void runSearch(ref);
  };

  const reset = () => {
    setRequest(null);
    setState("idle");
    setReference("");
    setError("");
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* stockage indisponible */
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <TrackHeader />

      <main>
        {/* Bandeau d'en-tête */}
        <section className="relative overflow-hidden pt-16">
          <img
            src="/images/accueil-classe.jpg"
            alt="Un enseignant accompagne des élèves en uniforme qui travaillent dans leurs cahiers"
            className="absolute inset-0 h-full w-full object-cover object-[center_30%]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/60 to-foreground-950/78"></div>

          <div className="relative z-10 mx-auto w-full max-w-5xl px-4 pb-32 pt-16 text-center md:px-8 md:pb-40 md:pt-24">
            <span className="inline-flex items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-3.5 py-1.5 text-xs font-medium text-background-50 backdrop-blur animate-slide-up">
              <i className="ri-route-line"></i>
              Suivi en temps réel
            </span>
            <h1 className="mt-5 text-3xl font-semibold font-heading leading-tight text-background-50 md:text-5xl animate-slide-up">
              Suivez l'état de votre demande
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base animate-slide-up">
              Saisissez la référence reçue lors du dépôt de votre demande pour connaître son
              avancement, sans compte ni mot de passe.
            </p>
          </div>
        </section>

        {/* Formulaire de recherche, chevauche le bandeau */}
        <section className="relative z-20 mx-auto -mt-20 w-full max-w-3xl px-4 md:-mt-24 md:px-8">
          <form
            onSubmit={handleSubmit}
            className="rounded-xl border border-background-200 bg-background-50 p-5 animate-scale-in md:p-7"
          >
            <label htmlFor="reference" className="block text-sm font-medium text-foreground-800">
              Référence de la demande
            </label>
            <p className="mt-1 text-xs text-foreground-500">
              Exemple : <span className="font-mono text-foreground-700">DEM-7K3Q9P</span> (affichée
              lors de l'envoi de la demande).
            </p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-base"></i>
                </span>
                <input
                  id="reference"
                  name="reference"
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Ex. DEM-7K3Q9P"
                  autoComplete="off"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <button
                type="submit"
                disabled={state === "loading"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:opacity-60 cursor-pointer"
              >
                {state === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Recherche...
                  </>
                ) : (
                  <>
                    <i className="ri-arrow-right-line"></i>
                    Suivre ma demande
                  </>
                )}
              </button>
            </div>
            {error && (
              <div className="mt-3 flex items-start gap-2.5 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-2.5">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-600">
                  <i className="ri-error-warning-line text-base"></i>
                </span>
                <p className="text-xs text-foreground-700">{error}</p>
              </div>
            )}
          </form>
        </section>

        {/* Résultat */}
        <section className="mx-auto w-full max-w-3xl px-4 py-10 md:px-8 md:py-14">
          {state === "found" && request && (
            <RequestStatusCard request={request} onNewSearch={reset} />
          )}

          {state === "notfound" && (
            <div className="animate-scale-in rounded-xl border border-background-200 bg-background-50 px-6 py-10 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-file-search-line text-2xl"></i>
              </span>
              <h2 className="mt-4 text-lg font-semibold font-heading text-foreground-950">
                Aucune demande trouvée
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
                Nous n'avons pas de demande correspondant à
                <span className="font-mono text-foreground-700"> {reference.trim()} </span>. Vérifiez
                la référence affichée lors de l'envoi, ou déposez une nouvelle demande.
              </p>
              <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
                >
                  <i className="ri-add-circle-line"></i>
                  Déposer une demande
                </Link>
                <button
                  onClick={reset}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Réessayer
                </button>
              </div>
            </div>
          )}

          {state === "idle" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {helpItems.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-100 text-primary-700">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-3.5 text-sm font-semibold font-heading text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{item.text}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <footer className="border-t border-background-200 bg-secondary-100">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-3 px-4 py-8 sm:flex-row md:px-8">
          <p className="text-xs text-foreground-500">
            © {new Date().getFullYear()} WaitechEdu. Tous droits réservés.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs font-medium text-foreground-600 hover:text-foreground-950 cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              Accueil
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-xs font-medium text-primary-700 hover:text-primary-800 cursor-pointer"
            >
              <i className="ri-login-box-line"></i>
              Se connecter
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function TrackRequest() {
  return <TrackContent />;
}